import { useEffect, useRef, useState } from 'react';

import { BoundingBox } from '@/types';

interface BoundingBoxSelectorProps {
  isActive: boolean;
  pageNumber: number;
  pageWidth: number;
  pageHeight: number;
  onSelectionComplete: (boundingBox: BoundingBox, imageData: string, selectionBox: SelectionBox) => void;
  maintainSelection?: boolean;
  activeSelection?: SelectionBox | null;
}

export interface SelectionBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export const BoundingBoxSelector = ({
  isActive,
  pageNumber,
  pageWidth,
  pageHeight,
  onSelectionComplete,
  maintainSelection = false,
  activeSelection,
}: BoundingBoxSelectorProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selection, setSelection] = useState<SelectionBox | null>(null);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isActive || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsSelecting(true);
    setSelection({
      startX: x,
      startY: y,
      endX: x,
      endY: y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isSelecting || !selection || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setSelection({
      ...selection,
      endX: x,
      endY: y,
    });
  };

  const handleMouseUp = async () => {
    if (!isSelecting || !selection || !canvasRef.current) return;

    setIsSelecting(false);

    // Normalize the selection box
    const left = Math.min(selection.startX, selection.endX);
    const top = Math.min(selection.startY, selection.endY);
    const width = Math.abs(selection.endX - selection.startX);
    const height = Math.abs(selection.endY - selection.startY);

    if (width < 10 || height < 10) {
      setSelection(null);
      return;
    }

    // Convert to relative coordinates (0-1 range)
    const boundingBox: BoundingBox = {
      left: left / pageWidth,
      top: top / pageHeight,
      width: width / pageWidth,
      height: height / pageHeight,
      pageNumber,
    };

    // Capture the selected area as image
    const pageElement = canvasRef.current.parentElement?.querySelector('img');
    if (pageElement && pageElement instanceof HTMLImageElement) {
      // Calculate scaling ratio between natural and displayed size. Use the
      // client rect (not img.width) so the page's zoom transform is included —
      // selection coordinates are in the same visual pixel space.
      const displayedRect = pageElement.getBoundingClientRect();
      const scaleX = pageElement.naturalWidth / displayedRect.width;
      const scaleY = pageElement.naturalHeight / displayedRect.height;

      // Convert selection coordinates to natural image coordinates
      const sourceX = left * scaleX;
      const sourceY = top * scaleY;
      const sourceWidth = width * scaleX;
      const sourceHeight = height * scaleY;

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = sourceWidth;
      tempCanvas.height = sourceHeight;
      const ctx = tempCanvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(pageElement, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, sourceWidth, sourceHeight);

        const imageData = tempCanvas.toDataURL('image/png').split(',')[1];
        const selectionBox: SelectionBox = {
          startX: left,
          startY: top,
          endX: left + width,
          endY: top + height,
        };
        onSelectionComplete(boundingBox, imageData, selectionBox);
      }
    }

    // Only clear selection if not maintaining it
    if (!maintainSelection) {
      setSelection(null);
    }
  };

  // Draw selection box
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const currentSelection = maintainSelection && activeSelection && !isSelecting ? activeSelection : selection;

    if (currentSelection && (isSelecting || (maintainSelection && activeSelection))) {
      const left = Math.min(currentSelection.startX, currentSelection.endX);
      const top = Math.min(currentSelection.startY, currentSelection.endY);
      const width = Math.abs(currentSelection.endX - currentSelection.startX);
      const height = Math.abs(currentSelection.endY - currentSelection.startY);

      ctx.strokeStyle = '#3B82F6';
      ctx.lineWidth = 2;
      ctx.strokeRect(left, top, width, height);

      // Fill with semi-transparent blue
      ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
      ctx.fillRect(left, top, width, height);
    }
  }, [selection, isSelecting, maintainSelection, activeSelection]);

  if (!isActive && !maintainSelection) return null;

  return (
    <canvas
      ref={canvasRef}
      width={pageWidth}
      height={pageHeight}
      className={`absolute inset-0 z-20 ${isActive && !maintainSelection ? 'cursor-crosshair' : 'pointer-events-none'}`}
      onMouseDown={isActive && !maintainSelection ? handleMouseDown : undefined}
      onMouseMove={isActive && !maintainSelection ? handleMouseMove : undefined}
      onMouseUp={isActive && !maintainSelection ? handleMouseUp : undefined}
      onMouseLeave={isActive && !maintainSelection ? handleMouseUp : undefined}
    />
  );
};
